import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  GALLERY_COLUMN_GAP,
  GALLERY_MAX_COLUMN_COUNT,
  GALLERY_MIN_COLUMN_WIDTH,
} from '../model/constants';
import FeedbackGalleryCard from './feedback-gallery-card';

type FeedbackGalleryGridProps = {
  feedbacks: FeedbackListItemT[];
  disableCardClick?: boolean;
};

const calcColumnCount = (containerWidth: number) => {
  if (containerWidth <= 0) return 1;
  const rawCount = Math.floor(
    (containerWidth + GALLERY_COLUMN_GAP) /
      (GALLERY_MIN_COLUMN_WIDTH + GALLERY_COLUMN_GAP),
  );
  return Math.max(1, Math.min(GALLERY_MAX_COLUMN_COUNT, rawCount));
};

const measureContainerWidth = (container: HTMLDivElement) => {
  return Math.max(
    container.clientWidth,
    container.getBoundingClientRect().width,
    container.parentElement?.clientWidth ?? 0,
  );
};

const FeedbackGalleryGrid = ({
  feedbacks,
  disableCardClick = false,
}: FeedbackGalleryGridProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(1);
  const gridStyle = useMemo(
    () => ({ columnGap: `${GALLERY_COLUMN_GAP}px` }),
    [],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateColumnCount = () => {
      const nextCount = calcColumnCount(measureContainerWidth(container));
      setColumnCount((prev) => (prev === nextCount ? prev : nextCount));
    };

    updateColumnCount();
    const rafId = window.requestAnimationFrame(() => {
      updateColumnCount();
    });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateColumnCount);
      observer.observe(container);
      return () => {
        window.cancelAnimationFrame(rafId);
        observer.disconnect();
      };
    }

    window.addEventListener('resize', updateColumnCount);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateColumnCount);
    };
  }, []);

  const renderColumnCount = Math.min(columnCount, feedbacks.length);
  const columns = Array.from(
    { length: renderColumnCount },
    () => [] as { feedback: FeedbackListItemT; index: number }[],
  );
  feedbacks.forEach((feedback, index) => {
    if (renderColumnCount === 0) return;
    columns[index % renderColumnCount].push({
      feedback,
      index,
    });
  });

  return (
    <div ref={containerRef} className="w-full min-w-0">
      {feedbacks.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-12">
          <span className="text-sm text-gray-400">No feedbacks found</span>
        </div>
      ) : (
        <div className="flex w-full min-w-0 items-start overflow-hidden" style={gridStyle}>
          {columns.map((columnFeedbacks, columnIdx) => (
            <div
              key={`feedback-gallery-column-${columnIdx}`}
              className="min-w-0 flex-1"
            >
              {columnFeedbacks.map((item) => (
                <FeedbackGalleryCard
                  key={item.feedback.alias}
                  feedback={item.feedback}
                  animationIndex={item.index}
                  disableClick={disableCardClick}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackGalleryGrid;
